import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import fetch from 'isomorphic-unfetch';

import {
  isWwsWeddingWebsiteV2,
  type WeddingWebsiteQuery,
} from '../types/graphql';
import Cookies from 'universal-cookie';

export interface WWQueryProps {
  data: WeddingWebsiteQuery;
  children?: ReactNode | undefined;
}

interface GetRegistryItemsResponse {
  data: RegistryItem[];
}

export interface RegistryItem {
  name: string;
  imageUrl: string;
  productId: number;
}

interface CoupleInfo {
  fianceFirstName: string;
  firstName: string;
  id: string;
  isActive: boolean;
  isCashRegistryActive: boolean;
  isDeleted: boolean;
  isHiddenProducts: boolean;
  isHiddenProductsInWebsite: boolean;
  isHiddenRegistriesInWebsite: boolean;
  isHiddenSearchEngines: boolean;
  isManualOverrided: boolean;
  isPrivate: boolean;
  registries: Registry[];
  stripeAccountStatus: string;
  charity: Charity | null;
  memberUuid: string;
  hasTransactionalRegistry: boolean;
}

interface Registry {
  id: string;
  registryUuid: string;
  registryType: number;
  hiddenCoupleSearch: boolean;
  hiddenWws: boolean;
  isDeleted: boolean;
  retailer: Retailer;
  manualRegistryName?: null | string | undefined;
  manualRegistryUrl?: null | string | undefined;
}

interface Retailer {
  id: number;
  retailerName: string;
  description: string;
  eventType: number;
  website: null | string;
  domain: null | string;
  additionalInfoUrl: null | string;
  storeInfoPageUrl: null | string;
  fullLogoImageUrl: string;
  smallLogoImageUrl: string;
  privacyPolicyUrl: string;
  registryCreateLogoImageUrl: null | string;
  returnAndExchangePolicy: null | string;
  oneColSortOrder: number;
  twoColSortOrder: null;
  threeColSortOrder: null;
  fourColSortOrder: number;
  isPartner: boolean;
  isDeleted: boolean;
  tier: number | null;
  benefits: null | string;
  slug: string;
}

interface Charity {
  charityId: string;
  charityName: string;
  personalMessage: string;
  ein: string;
  charityUrl: string;
  charityTypeCode: string;
  isDeleted: boolean;
}

export interface RegistryContextValue {
  coupleInfo?: CoupleInfo | undefined;
  registryItems: RegistryItem[];
  areRegistryItemsLoading: boolean;
}

const getCoupleInfo = async (
  data: WeddingWebsiteQuery
): Promise<CoupleInfo | undefined> => {
  let coupleInfo: CoupleInfo | undefined;
  if (
    data?.weddingWebsiteResult?.__typename === 'WWS_WeddingWebsiteV2' &&
    data.weddingWebsiteResult.userUuid
  ) {
    try {
      // Actually check for status code 🤦‍♂️
      const apiData: CoupleInfo = await fetch(
        `${process.env['REGISTRY_CORE']}/v2/members/${data.weddingWebsiteResult.userUuid}/public-summary?eventType=wedding`
      )
        .then((resp: Response): Promise<CoupleInfo> => resp.json())
        .catch((error: Error): never => {
          console.error(error);

          throw new Error(error.message);
        });

      coupleInfo = apiData;
    } catch (error: unknown) {
      console.error(error);
    }
  }

  return coupleInfo;
};

const getRegistryItems = async (data: WeddingWebsiteQuery): Promise<any[]> => {
  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return [];
  }

  const host = process.env['REGISTRY_ITEM_GATEWAY_API'];
  const memberId = data?.weddingWebsiteResult.userUuid;
  const itemLimit = 25;
  const url = `${host}/items?limit=${itemLimit}&memberId=${memberId}&showCash=true&registryStatusFilter=active,visible&sort=retailerSortOrder-asc&showGiftCards=true&showDisabled=false`;
  const cookies = new Cookies();
  const getRegistryItemsResponse: GetRegistryItemsResponse = await fetch(url, {
    headers: { Cookie: cookies.get('_abck') },
  })
    .then((resp: Response): Promise<GetRegistryItemsResponse> => resp.json())
    .catch((_: Error) => {
      return { data: [] };
    });

  return getRegistryItemsResponse.data;
};

const RegistryContext = createContext<RegistryContextValue | null>(null);

export function RegistryProvider({ children, data }: WWQueryProps) {
  const [coupleInfo, setCoupleInfo] = useState<CoupleInfo | undefined>(
    undefined
  );

  const [registryItems, setRegistryItems] = useState<RegistryItem[]>([]);
  const [areRegistryItemsLoading, setAreRegistryItemsLoading] =
    useState<boolean>(false);

  useEffect((): void => {
    getCoupleInfo(data).then(setCoupleInfo);
  }, [data.weddingWebsiteResult]);

  useEffect((): void => {
    setAreRegistryItemsLoading(true);
    getRegistryItems(data)
      .then((items) => {
        setRegistryItems(items);
      })
      .finally(() => setAreRegistryItemsLoading(false));
  }, [data.weddingWebsiteResult]);

  return (
    <RegistryContext.Provider
      value={{ coupleInfo, registryItems, areRegistryItemsLoading }}
    >
      {children}
    </RegistryContext.Provider>
  );
}

export const useRegistryContext = () => {
  const context = useContext(RegistryContext);

  if (!context) {
    throw new Error(
      'useRegistryContext must be used within a RegistryProvider'
    );
  }

  return context;
};
